/**
 * Choosing what the room plays next, without anybody leaving it.
 *
 * The room is the durable thing: same code, same socket, same people, same
 * seats — the game inside it changes. Before this, finishing a game left a room
 * with nothing to be, and playing something else meant everybody reconnecting.
 *
 * Host only. The server refuses it from anybody else, so offering it would be
 * offering a button that fails.
 */

import { useState } from 'react';
import type { MatchView } from '../net/match.js';
import { useCatalog } from '../use-catalog.js';
import { playerCount } from '../render/players.js';
import { shelve } from '../render/shelves.js';
import { Icon } from '../render/Icon.js';
import { GameCard } from './GameCard.js';

export function GamePicker({
  view,
  onHostAction,
}: {
  view: MatchView;
  onHostAction: (action: Record<string, unknown>) => void;
}) {
  const { catalog } = useCatalog();
  const [open, setOpen] = useState(false);

  if (!view.isHost) return null;

  /*
   * Only what this host can actually start.
   *
   * `owned` covers free games too, and `maxSeats` is zero for a game their
   * tier cannot seat — so a game that would be refused is never offered.
   */
  const playable = (catalog?.games ?? []).filter(
    (game) => game.owned && game.maxSeats >= game.minPlayers,
  );

  if (playable.length < 2) return null;

  /*
   * Closed, this is a row that says what the setting IS and changes it.
   *
   * The WHOLE row is the button. It looked like one already - a full-width
   * bordered block with an icon, a title and the game's name - with a small
   * "Change" pill at the right that was the only part that did anything. So
   * the thing that looked pressable was not, and the thing that was is a
   * fraction of its width on a phone.
   *
   * The chevron stays, because a row that does something should say so. It is
   * a mark rather than a target now: the target is the row.
   */
  if (!open) {
    const current = playable.find((game) => game.id === view.gameId);
    return (
      <button type="button" className="setting-row setting-action" onClick={() => setOpen(true)}>
        <span className="setting-icon">
          <Icon name="gamepad" />
        </span>
        <span className="setting-text">
          <span className="setting-title">Game</span>
          <span className="setting-help">{current?.name ?? 'Choose one'}</span>
        </span>
        <span className="setting-go" aria-hidden="true">
          Change
          <Icon name="chevron-right" />
        </span>
      </button>
    );
  }

  /*
   * Open, this is the library's shelves and the library's cards.
   *
   * It was a flat list of names with a line of metadata under each - every
   * game looking like every other game, in the one moment somebody is
   * choosing between them. The library solved that already with art, so this
   * asks the same `shelve` for the same groups and draws the same `GameCard`.
   * The only difference is what a press means, which is a prop on the card.
   */
  const shelves = shelve(playable);

  return (
    <div className="game-picker">
      <div className="game-picker-head">
        <span className="setting-group-label">Play something else</span>
        <button type="button" className="text-button" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>

      {shelves.map((shelf) => (
        <section className="game-picker-shelf" key={shelf.id}>
          {shelf.title === null ? null : <h3 className="shelf-title">{shelf.title}</h3>}
          <div className="game-picker-grid">
            {shelf.games.map((game) => {
              const current = game.id === view.gameId;
              /*
               * Said here rather than discovered at Start.
               *
               * Switching does not move anybody, so a game that cannot seat
               * the people already here needs somebody sent to watching
               * first - and that is worth knowing before the room changes
               * under them.
               */
              const fits = view.seated >= game.minPlayers && view.seated <= game.maxSeats;
              const range = playerCount(game.minPlayers, game.maxPlayers);

              return (
                <GameCard
                  key={game.id}
                  game={game}
                  disabled={current}
                  note={current ? 'Playing now' : fits ? undefined : `Needs ${range}`}
                  onPick={() => {
                    onHostAction({ action: 'SET_GAME', gameId: game.id });
                    setOpen(false);
                  }}
                />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
